import { useBreakpoint } from '@hooks/useBreakpoint'
import * as RadixDialog from '@radix-ui/react-dialog'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import React, { ReactNode } from 'react'
import { HiXMark } from 'react-icons/hi2'
import { cn } from './cn'
import { IconButton } from './IconButton'
import { useLatched } from './useLatched'

const EASE_OUT = [0.22, 1, 0.36, 1] as const

export interface SheetProps {
  open: boolean
  onClose?: () => void
  /** bottom: slides up on phones, centred card from `sm`. left: the mobile sidebar. */
  side?: 'bottom' | 'left'
  /** Shown as the sheet heading. Always give one; use `hideTitle` to keep it for screen readers only. */
  title?: ReactNode
  hideTitle?: boolean
  description?: ReactNode
  /** Close button in the header. */
  closable?: boolean
  /** Escape and backdrop clicks close the sheet (default true). */
  dismissible?: boolean
  className?: string
  children?: ReactNode
}

/** Modal sheet on Radix Dialog (focus trap, Escape, aria) with framer-motion for the slide. */
export const Sheet = ({
  open,
  onClose,
  side = 'bottom',
  title,
  hideTitle,
  description,
  closable,
  dismissible = true,
  className,
  children,
}: SheetProps) => {
  const content = useLatched(open, { title, description, children })
  const wide = useBreakpoint('sm')
  const reduce = useReducedMotion()
  const centred = side === 'bottom' && wide

  const panelMotion = reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : side === 'left'
      ? { initial: { x: '-100%' }, animate: { x: 0 }, exit: { x: '-100%' } }
      : centred
        ? {
            initial: { opacity: 0, scale: 0.96, y: 8 },
            animate: { opacity: 1, scale: 1, y: 0 },
            exit: { opacity: 0, scale: 0.97, y: 8 },
          }
        : { initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' } }

  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => !next && onClose?.()}>
      <AnimatePresence>
        {open && (
          <RadixDialog.Portal forceMount>
            <RadixDialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[100] bg-scrim backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </RadixDialog.Overlay>
            <div
              className={cn(
                'pointer-events-none fixed inset-0 z-[101] flex',
                side === 'left'
                  ? 'justify-start'
                  : centred
                    ? 'items-center justify-center p-6'
                    : 'items-end'
              )}
            >
              <RadixDialog.Content
                asChild
                forceMount
                onEscapeKeyDown={(e) => !dismissible && e.preventDefault()}
                onPointerDownOutside={(e) => !dismissible && e.preventDefault()}
                onInteractOutside={(e) => !dismissible && e.preventDefault()}
              >
                <motion.div
                  {...panelMotion}
                  transition={{ duration: reduce ? 0.15 : 0.32, ease: EASE_OUT }}
                  className={cn(
                    'plug-sheet-surface pointer-events-auto flex flex-col overflow-y-auto bg-sheet text-fg shadow-md outline-none no-scrollbar',
                    'border border-solid border-hairline',
                    side === 'left' && 'h-full w-[300px] max-w-[85vw] border-y-0 border-l-0',
                    side === 'bottom' &&
                      !centred &&
                      'max-h-[90dvh] w-full rounded-t-xl border-b-0 px-5 pt-3 pb-[max(24px,env(safe-area-inset-bottom))]',
                    centred &&
                      'max-h-[calc(100dvh-96px)] w-full max-w-[480px] rounded-xl px-6 pt-5 pb-6',
                    className
                  )}
                >
                  {side === 'bottom' && !centred && (
                    <div
                      aria-hidden="true"
                      className="mx-auto mb-3 h-1 w-9 shrink-0 rounded-pill bg-hairline-strong"
                    />
                  )}
                  {(content.title || closable) && (
                    <div
                      className={cn(
                        'mb-4 flex items-center gap-3',
                        hideTitle && !closable && 'mb-0'
                      )}
                    >
                      <RadixDialog.Title
                        className={cn(
                          'm-0 flex-1 font-display text-[20px] leading-[1.3] font-semibold tracking-[-0.01em]',
                          hideTitle && 'sr-only'
                        )}
                      >
                        {content.title}
                      </RadixDialog.Title>
                      {closable && (
                        <RadixDialog.Close asChild>
                          <IconButton aria-label="Close" size="sm" className="-mr-1 ml-auto">
                            <HiXMark />
                          </IconButton>
                        </RadixDialog.Close>
                      )}
                    </div>
                  )}
                  {!content.title && (
                    <RadixDialog.Title className="sr-only">Dialog</RadixDialog.Title>
                  )}
                  {content.description ? (
                    <RadixDialog.Description className="m-0 mb-4 text-[14px] leading-[1.5] text-fg-2">
                      {content.description}
                    </RadixDialog.Description>
                  ) : (
                    <RadixDialog.Description className="sr-only" />
                  )}
                  {content.children}
                </motion.div>
              </RadixDialog.Content>
            </div>
          </RadixDialog.Portal>
        )}
      </AnimatePresence>
    </RadixDialog.Root>
  )
}
